import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
	ensureRepertoireEntry,
	getStudentRepertoire,
} from '../../../../services/RepertoireService';
import { fetchNotesForStudent } from '../../../../services/SongNotesService';
import { mapLegacyStatus, WARMUPS_SONG_ID } from '../../../../services/songNotesModel';

/**
 * The song board: the (student × song) working set for one lesson session.
 *
 * Extracted from SongNotes.js (N1b) as a logic MOVE, not a rewrite — the
 * hydration join, the hydratingRef guard, and the repertoire-id discipline all
 * carry over intact. Presentation is rebuilt separately (design handoff
 * 2026-07-22); this hook owns only state.
 *
 * Shape of a board song: { id, name, metadata, lyrics, videos, notes: [] } where `id`
 * is the REPERTOIRE DOCUMENT ID — that is what a Song Note's `songId` points
 * at. The one exception is the built-in Warm-ups pseudo-song (WARMUPS_SONG_ID
 * sentinel, no repertoire doc): it is always present, always first, and is
 * where song-less jots land so the jot bar is never disabled.
 */

/** The built-in Warm-ups tab. Recreated fresh per board (notes attach on hydrate). */
const makeWarmupsSong = () => ({
	id: WARMUPS_SONG_ID,
	name: 'Warm-ups',
	metadata: null,
	lyrics: null,
	videos: {},
	notes: [],
	builtIn: true,
});

const useSongBoard = ({ studentId, initialSongId = null }) => {
	const [songs, setSongs] = useState(() => [makeWarmupsSong()]);
	// `initialSongId` restores the tab a teacher was on before they navigated
	// away (useLessonScratch). It is a SEED, not a controlled prop: the board
	// owns the selection once mounted, and a song that no longer exists simply
	// falls back to Warm-ups through the currentSong lookup below.
	const [currentSongId, setCurrentSongId] = useState(initialSongId || WARMUPS_SONG_ID);
	const [isLoadingNotes, setIsLoadingNotes] = useState(false);
	const [loadError, setLoadError] = useState('');

	// Set while hydrating from Firestore so the resulting setSongs is not
	// mistaken for a teacher edit (see SongNotes.js N1b — merely OPENING a
	// lesson must not stamp every song as "worked today").
	const hydratingRef = useRef(false);

	// Notes persist as their own documents whenever we know which student they
	// belong to. Without a studentId there is no (student × song) key, so the
	// board runs local-only — the unlinked-lesson case. Jots still work; they
	// hold locally and flush when a student is linked (see useSongNotes).
	const isPersistent = Boolean(studentId);

	/**
	 * Hydrate songs + notes from Firestore: song list from `repertoire`, notes
	 * from `songNotes`, joined on songId. Warm-ups is prepended and receives any
	 * notes keyed to the sentinel. Legacy note statuses normalise on read.
	 */
	const loadPersistedNotes = useCallback(async () => {
		if (!studentId) return;
		setIsLoadingNotes(true);
		setLoadError('');
		try {
			const [repertoire, notes] = await Promise.all([
				getStudentRepertoire(studentId),
				fetchNotesForStudent(studentId),
			]);

			const notesBySong = new Map();
			for (const note of notes) {
				if (!notesBySong.has(note.songId)) notesBySong.set(note.songId, []);
				notesBySong.get(note.songId).push({ ...note, status: mapLegacyStatus(note) });
			}

			hydratingRef.current = true;
			setSongs([
				{ ...makeWarmupsSong(), notes: notesBySong.get(WARMUPS_SONG_ID) || [] },
				...repertoire.map((entry) => ({
					id: entry.id,
					name: entry.songName,
					metadata: entry.metadata || null,
					// Registry id, so the track strip joins on the same key the
					// Songs page does; null = unlinked, and the name fallback applies.
					canonicalSongId: entry.canonicalSongId || null,
					lyrics: entry.lyrics || null,
					// Song sections (2026-09-25), written by labelSongSections.
					sections: entry.sections || null,
					sectionsMeta: entry.sectionsMeta || null,
					// A pre-2026-08-31 row has a single `videoUrl` and no
					// `videos` map. It is surfaced in the lyrics slot because
					// that was the picker's default intent when it was saved —
					// a guess, but a recoverable one: re-saving it under the
					// right kind is one click, and dropping it silently is not.
					videos: entry.videos || (entry.videoUrl ? { lyrics: entry.videoUrl } : {}),
					// Recital / Audition tracks (2026-09-27, trackRolesModel).
					recitalTrack: entry.recitalTrack || null,
					auditionTracks: entry.auditionTracks || [],
					// Section nudges and trims (2026-09-28) are saved on the row
					// and read from the board; until 2026-10-02 they were not
					// copied here, so both vanished on the next page load.
					sectionMarks: entry.sectionMarks || {},
					trims: entry.trims || {},
					notes: notesBySong.get(entry.id) || [],
				})),
			]);
		} catch (error) {
			console.error('Error loading Song Notes:', error);
			setLoadError('Could not load notes. Refresh to try again.');
		} finally {
			setIsLoadingNotes(false);
		}
	}, [studentId]);

	useEffect(() => {
		if (isPersistent) loadPersistedNotes();
	}, [isPersistent, loadPersistedNotes]);

	const currentSong = useMemo(
		() => songs.find((s) => s.id === currentSongId) || songs[0] || null,
		[songs, currentSongId]
	);

	/**
	 * Add a song to the board (already-resolved metadata; the search flow lives
	 * in useSongMetadataSearch). The id MUST be the repertoire doc id — minting
	 * local ids per lesson is exactly how the same song in two lessons used to
	 * grow two unrelated note histories. Re-adding an existing song re-attaches
	 * its history: ensureRepertoireEntry returns the existing row.
	 */
	const addSong = useCallback(
		async (songName, metadata = null) => {
			let songId = `local-${Date.now()}`;
			// The registry id, so a new song can look for other students' setup
			// of it (songReuseService) and its tracks join on the same key.
			let canonicalSongId = null;

			if (isPersistent) {
				const res = await ensureRepertoireEntry(studentId, songName, metadata);
				songId = res.id;
				canonicalSongId = res.canonicalSongId || null;
			}

			let result = { id: songId, existed: false, canonicalSongId };
			setSongs((prev) => {
				const existing = prev.find((s) => s.id === songId);
				if (existing) {
					result = { id: songId, existed: true, canonicalSongId };
					return prev;
				}
				return [...prev, { id: songId, name: songName, metadata: metadata || null, canonicalSongId, lyrics: null, videos: {}, notes: [] }];
			});
			setCurrentSongId(songId);
			return result;
		},
		[isPersistent, studentId]
	);

	/**
	 * Remove a song from THIS BOARD only — never the repertoire entry, never its
	 * notes. Notes are the durable record across teachers and years; a mis-tap
	 * must not be able to destroy them. Warm-ups is built-in and cannot go.
	 *
	 * Returns what was removed ({ song, index, wasCurrent }, or null) so the
	 * page can offer Undo (pass 3 A7): restoreSongToBoard puts it back where it
	 * was. Nothing is written either way; the board is this lesson's state.
	 */
	const removeSongFromBoard = useCallback(
		(songId) => {
			if (songId === WARMUPS_SONG_ID) return null;
			const index = songs.findIndex((s) => s.id === songId);
			const removed = index >= 0 ? { song: songs[index], index, wasCurrent: songId === currentSongId } : null;
			setSongs((prev) => {
				const next = prev.filter((s) => s.id !== songId);
				if (songId === currentSongId) {
					setCurrentSongId(next[0]?.id ?? WARMUPS_SONG_ID);
				}
				return next;
			});
			return removed;
		},
		[songs, currentSongId]
	);

	/** Undo a removal: back at its old position, and current again if it was. */
	const restoreSongToBoard = useCallback((removed) => {
		if (!removed || !removed.song) return;
		const { song, index, wasCurrent } = removed;
		setSongs((prev) => {
			if (prev.some((s) => s.id === song.id)) return prev;
			const at = Math.min(Math.max(index, 0), prev.length);
			return [...prev.slice(0, at), song, ...prev.slice(at)];
		});
		if (wasCurrent) setCurrentSongId(song.id);
	}, []);

	return {
		songs,
		setSongs,
		currentSong,
		currentSongId,
		setCurrentSongId,
		studentId,
		isPersistent,
		isLoadingNotes,
		loadError,
		reload: loadPersistedNotes,
		addSong,
		removeSongFromBoard,
		restoreSongToBoard,
		hydratingRef,
	};
};

export default useSongBoard;
